import { sendEvent, SendEventParams } from '@/utils/sendEvent';
import { GomarkePixelConfig, PixelResponse } from '@/types';

/**
 * Tipos de seletores suportados
 */
export type SelectorType =
  | 'class'
  | 'id'
  | 'attribute'
  | 'tag'
  | 'text'
  | 'url'
  | 'data-attribute'
  | 'custom'
  | 'automatic';

/**
 * Configuração base para autoTracks
 */
export interface IAutoTrackerConfig {
  isEnabled: boolean;
  selector: string;
  selectorType: SelectorType;
  debounceMs?: number;
  throttleMs?: number;
  retryAttempts?: number;
  retryDelay?: number;
}

/**
 * Dados de evento capturado
 */
export interface ICapturedEvent {
  element: Element;
  event: Event;
  timestamp: number;
  selector: string;
  selectorType: SelectorType;
  customData?: Record<string, unknown>;
}

/**
 * Callback para processamento de eventos
 */
export type EventCallback = (
  capturedEvent: ICapturedEvent
) => Promise<PixelResponse> | void;

/**
 * Classe base para todos os autoTracks
 */
export abstract class BaseAutoTracker {
  protected baseConfig: GomarkePixelConfig;
  protected sessionId: string;
  protected version: string;
  protected isInitialized: boolean = false;
  protected eventListeners: Map<
    string,
    (event: Event) => Promise<void> | void
  > = new Map();
  /** Entradas para remoção exata de listeners (elemento + tipo + fn). */
  protected eventListenerEntries: Array<{
    element: Element;
    eventType: string;
    listener: (event: Event) => void;
    capture?: boolean;
  }> = [];
  protected observer?: MutationObserver;

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    visitorId: string = ''
  ) {
    this.baseConfig = config;
    this.sessionId = sessionId;
    this.version = version;
    this.visitorId = visitorId;
  }

  protected visitorId: string;

  /**
   * Inicializa o autoTracker
   */
  public abstract initialize(autoConfig: IAutoTrackerConfig): Promise<void>;

  /**
   * Para o autoTracker
   */
  public abstract stop(): void;

  /**
   * Reconstrói os listeners (útil para DOM dinâmico)
   */
  public abstract rebuild(): Promise<void>;

  /**
   * Envia evento para a API
   */
  protected async sendEvent({
    category,
    type,
    eventData,
    config,
    sessionId,
    visitorId,
    version,
    scriptUrl,
  }: SendEventParams): Promise<PixelResponse> {
    return sendEvent({
      category,
      type,
      eventData,
      config,
      sessionId,
      visitorId: visitorId ?? this.visitorId,
      version,
      scriptUrl,
    });
  }
  /**
   * Obtém elementos baseado no seletor
   */
  protected getElements(
    selector: string,
    selectorType: SelectorType
  ): NodeList {
    const fullSelector = this.buildSelector(selector, selectorType);

    if (this.baseConfig.debug) {
      console.log(`Buscando elementos com seletor: ${fullSelector}`);
    }

    return document.querySelectorAll(fullSelector);
  }

  /**
   * Constrói o seletor CSS completo
   */
  protected buildSelector(
    selector: string,
    selectorType: SelectorType
  ): string {
    switch (selectorType) {
      case 'class':
        return `.${selector}`;
      case 'id':
        return `#${selector}`;
      case 'attribute':
        return `[${selector}]`;
      case 'data-attribute':
        return `[data-${selector}]`;
      case 'tag':
        return selector;
      case 'text':
        return `:contains(${selector})`;
      case 'url':
        return `[href="${selector}"]`;
      case 'custom':
        return selector;
      default:
        return selector;
    }
  }

  /**
   * Adiciona listener com debounce/throttle
   */
  protected addListenerWithTiming(
    element: Element,
    eventType: string,
    callback: EventCallback,
    config: IAutoTrackerConfig,
    capture: boolean = false
  ): void {
    let timeoutId: ReturnType<typeof setTimeout>;
    let lastExecution = 0;

    const wrappedCallback = async (event: Event) => {
      const now = Date.now();

      // Debounce
      if (config.debounceMs) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          this.executeCallback(element, event, callback, config);
        }, config.debounceMs);
        return;
      }

      // Throttle
      if (config.throttleMs && now - lastExecution < config.throttleMs) {
        return;
      }

      lastExecution = now;
      await this.executeCallback(element, event, callback, config);
    };

    const listenerKey = `${element.tagName}-${eventType}-${Date.now()}`;
    this.eventListeners.set(listenerKey, wrappedCallback);
    this.eventListenerEntries.push({
      element,
      eventType,
      listener: wrappedCallback,
      capture,
    });
    element.addEventListener(eventType, wrappedCallback, capture);
  }

  /**
   * Executa o callback com tratamento de erro
   */
  protected async executeCallback(
    element: Element,
    event: Event,
    callback: EventCallback,
    config: IAutoTrackerConfig
  ): Promise<void> {
    try {
      const capturedEvent: ICapturedEvent = {
        element,
        event,
        timestamp: Date.now(),
        selector: config.selector,
        selectorType: config.selectorType,
        customData: this.extractCustomData(element),
      };

      await callback(capturedEvent);
    } catch (error) {
      if (this.baseConfig.debug) {
        console.error('Erro no autoTracker:', error);
      }

      // Retry logic
      if (config.retryAttempts && config.retryAttempts > 0) {
        setTimeout(() => {
          this.executeCallback(element, event, callback, {
            ...config,
            retryAttempts: (config.retryAttempts || 0) - 1,
          });
        }, config.retryDelay || 1000);
      }
    }
  }

  /**
   * Extrai dados customizados do elemento
   */
  protected extractCustomData(element: Element): Record<string, unknown> {
    const data: Record<string, unknown> = {};

    // Extrai data-attributes
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('data-')) {
        data[attr.name] = attr.value;
      }
    });

    // Extrai texto do elemento
    data.textContent = element.textContent?.trim();

    // Extrai classes
    if (element.className) {
      data.classes = element.className.split(' ');
    }

    return data;
  }

  /**
   * Observa mudanças no DOM para elementos dinâmicos
   */
  protected observeDOM(callback: () => void): void {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new MutationObserver(mutations => {
      let shouldRebuild = false;

      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldRebuild = true;
        }
      });

      if (shouldRebuild) {
        callback();
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Limpa todos os listeners (remove exatamente do elemento registrado).
   */
  protected clearListeners(): void {
    this.eventListenerEntries.forEach(
      ({ element, eventType, listener, capture }) => {
        try {
          element.removeEventListener(eventType, listener, capture);
        } catch {
          // elemento pode ter sido removido do DOM
        }
      }
    );
    this.eventListenerEntries.length = 0;
    this.eventListeners.clear();

    if (this.observer) {
      this.observer.disconnect();
      this.observer = undefined;
    }
  }

  /**
   * Verifica se o autoTracker está ativo
   */
  public isActive(): boolean {
    return this.isInitialized && this.eventListeners.size > 0;
  }

  /**
   * Obtém estatísticas do autoTracker
   */
  public getStats(): {
    isActive: boolean;
    listenersCount: number;
    isObserving: boolean;
  } {
    return {
      isActive: this.isActive(),
      listenersCount: this.eventListeners.size,
      isObserving: !!this.observer,
    };
  }
}
